/**
 * MD-11 Phase 2 - Consolidated ChatHistory component (Direction A redesign).
 *
 * Replaces 2 duplicate file (ChatHistorySidebar.tsx 448 LOC + ChatHistoryDrawer.tsx
 * 446 LOC) - 90% overlap logic. Per F5 dedup rule + memory
 * `feedback_reuse_existing_code`: 1 feature = 1 component.
 *
 * Variant API:
 *   - variant="sidebar": 280px fixed left rail, always visible desktop, no overlay.
 *     Self-managed: no isOpen/onClose needed.
 *   - variant="drawer":  320px slide-in overlay (mobile/controlled).
 *     Caller owns state via isOpen + onClose. Hidden when !isOpen.
 *
 * Shared logic:
 *   - Anon localStorage helpers (load/save/upsert/delete)
 *   - Authed API fetch (GET /api/v1/me/chat, /:sid, DELETE /:sid)
 *   - Group by date (today / yesterday / week / older), relative time
 *   - Delete + confirm + new chat + search
 *
 * A11y:
 *   - role=navigation + aria-label
 *   - active item aria-current=page
 *   - keyboard: Tab + Enter + Delete (confirm), ESC close drawer
 *   - drawer: backdrop click + ESC close
 *
 * Phase 2 Track C (2026-05-22).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { Button, Input } from '../primitives';

// ---- Types (exported for chat.tsx callers) ----

export type SidebarMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: unknown;
  created_at: string;
};

export type SidebarSession = {
  session_id: string;
  title: string | null;
  last_turn_at: string;
  turn_count: number;
  created_at: string;
};

export type AnonThread = {
  id: string;
  title: string;
  created_at: string;
  last_message_at: string;
  messages: SidebarMessage[];
};

const ANON_LIST_KEY = 'asklaw_anon_threads';
const ANON_LIST_CAP = 50;

// ---- Anon localStorage helpers (re-exported for chat.tsx callers) ----

export function loadAnonThreadList(): AnonThread[] {
  try {
    const raw = window.localStorage.getItem(ANON_LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AnonThread[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t) =>
        t &&
        typeof t.id === 'string' &&
        typeof t.title === 'string' &&
        Array.isArray(t.messages)
    );
  } catch {
    return [];
  }
}

export function saveAnonThreadList(threads: AnonThread[]): void {
  try {
    const capped = threads.slice(0, ANON_LIST_CAP);
    window.localStorage.setItem(ANON_LIST_KEY, JSON.stringify(capped));
  } catch {
    // quota / disabled
  }
}

export function upsertAnonThread(thread: AnonThread): void {
  const list = loadAnonThreadList();
  const idx = list.findIndex((t) => t.id === thread.id);
  if (idx >= 0) {
    list[idx] = thread;
  } else {
    list.unshift(thread);
  }
  list.sort((a, b) => (a.last_message_at < b.last_message_at ? 1 : -1));
  saveAnonThreadList(list);
}

export function deleteAnonThread(id: string): void {
  const list = loadAnonThreadList().filter((t) => t.id !== id);
  saveAnonThreadList(list);
}

/**
 * BUG-2026-06-12 Bug 3 (duplicate sidebar session): resolve the sidebar thread
 * id for a rehydrated anon thread. Prefer the persisted thread_id; legacy
 * stores (pre-fix) lack it, so fall back to matching the rehydrated first
 * message id against the saved thread list. Returns null when no identity can
 * be proven (caller keeps currentSessionId = null and mints a fresh id on the
 * next send, the pre-fix behavior).
 */
export function resolveAnonThreadId(
  storedThreadId: string | undefined,
  firstMessageId: string | undefined,
  threads: AnonThread[]
): string | null {
  if (storedThreadId) return storedThreadId;
  if (!firstMessageId) return null;
  const match = threads.find((t) => t.messages[0]?.id === firstMessageId);
  return match ? match.id : null;
}

// ---- Time + group helpers ----

function relativeTime(iso: string, lang: 'vi' | 'en'): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '';
  const now = Date.now();
  const diffMs = now - ts;
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (lang === 'vi') {
    if (minutes < 1) return 'Vừa xong';
    if (minutes < 60) return `${minutes} phút trước`;
    if (hours < 24) return `${hours} giờ trước`;
    if (days === 1) return 'Hôm qua';
    if (days < 7) return `${days} ngày trước`;
    return new Date(iso).toLocaleDateString('vi-VN');
  }
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US');
}

type GroupKey = 'today' | 'yesterday' | 'week' | 'older';

function groupKey(iso: string): GroupKey {
  const ts = Date.parse(iso);
  const days = Math.floor((Date.now() - ts) / 86400000);
  if (days < 1) return 'today';
  if (days < 2) return 'yesterday';
  if (days < 7) return 'week';
  return 'older';
}

function groupLabel(key: GroupKey, lang: 'vi' | 'en'): string {
  if (lang === 'vi') {
    return {
      today: 'Hôm nay',
      yesterday: 'Hôm qua',
      week: 'Tuần này',
      older: 'Cũ hơn',
    }[key];
  }
  return {
    today: 'Today',
    yesterday: 'Yesterday',
    week: 'Previous 7 days',
    older: 'Older',
  }[key];
}

// ---- Icons ----

const iconCommon = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconClose(): ReactElement {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...iconCommon}
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function IconTrash(): ReactElement {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...iconCommon}
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
    </svg>
  );
}

function IconPlus(): ReactElement {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...iconCommon}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

// ---- Props ----

type BaseProps = {
  lang: 'vi' | 'en';
  isAuthed: boolean;
  accessToken: string | null;
  currentSessionId: string | null;
  onSelectSession: (session_id: string, messages: SidebarMessage[]) => void;
  onNewChat: () => void;
  /** Bump to force reload after persisting a new message. */
  refreshTick?: number;
};

type SidebarProps = BaseProps & {
  variant: 'sidebar';
  isOpen?: never;
  onClose?: never;
};

type DrawerProps = BaseProps & {
  variant: 'drawer';
  isOpen: boolean;
  onClose: () => void;
};

export type ChatHistoryProps = SidebarProps | DrawerProps;

// ---- Component ----

export function ChatHistory(props: ChatHistoryProps): ReactElement | null {
  const {
    variant,
    lang,
    isAuthed,
    accessToken,
    currentSessionId,
    onSelectSession,
    onNewChat,
    refreshTick,
  } = props;
  const isDrawer = variant === 'drawer';
  const isOpen = isDrawer ? props.isOpen : true;
  const onClose = isDrawer ? props.onClose : undefined;

  const [sessions, setSessions] = useState<SidebarSession[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);

  // ---- Load list ----

  const loadList = useCallback(async () => {
    setError(null);
    if (!isAuthed) {
      const threads = loadAnonThreadList();
      setSessions(
        threads.map((t) => ({
          session_id: t.id,
          title: t.title,
          last_turn_at: t.last_message_at,
          turn_count: t.messages.filter((m) => m.role === 'user').length,
          created_at: t.created_at,
        }))
      );
      return;
    }
    if (!accessToken) {
      setSessions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/v1/me/chat?limit=50', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setSessions([]);
        return;
      }
      const data = (await res.json()) as { sessions: SidebarSession[] };
      setSessions(data.sessions ?? []);
    } catch (e) {
      setError((e as Error).message);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthed, accessToken]);

  // Reload on open/refresh.
  useEffect(() => {
    if (isDrawer && !isOpen) return;
    loadList();
  }, [loadList, refreshTick, isDrawer, isOpen]);

  // ESC closes drawer.
  useEffect(() => {
    if (!isDrawer || !isOpen || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDrawer, isOpen, onClose]);

  // ---- Filter + group ----

  const grouped = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? sessions.filter((s) => (s.title ?? '').toLowerCase().includes(needle))
      : sessions;
    const buckets: Record<GroupKey, SidebarSession[]> = {
      today: [],
      yesterday: [],
      week: [],
      older: [],
    };
    for (const s of filtered) {
      buckets[groupKey(s.last_turn_at)].push(s);
    }
    return buckets;
  }, [sessions, search]);

  // ---- Handlers ----

  const handleSelect = useCallback(
    async (sid: string) => {
      if (!isAuthed) {
        const threads = loadAnonThreadList();
        const t = threads.find((x) => x.id === sid);
        onSelectSession(sid, t?.messages ?? []);
        return;
      }
      if (!accessToken) return;
      try {
        const res = await fetch(`/api/v1/me/chat/${encodeURIComponent(sid)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          setError(`load HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as { messages: SidebarMessage[] };
        onSelectSession(sid, data.messages ?? []);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [isAuthed, accessToken, onSelectSession]
  );

  const handleDelete = useCallback(
    async (sid: string, ev: React.MouseEvent | React.KeyboardEvent) => {
      ev.stopPropagation();
      const ok = window.confirm(
        lang === 'vi' ? 'Xoá cuộc trò chuyện này?' : 'Delete this conversation?'
      );
      if (!ok) return;
      if (!isAuthed) {
        deleteAnonThread(sid);
        await loadList();
        if (sid === currentSessionId) onNewChat();
        return;
      }
      if (!accessToken) return;
      try {
        const res = await fetch(`/api/v1/me/chat/${encodeURIComponent(sid)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          await loadList();
          if (sid === currentSessionId) onNewChat();
        } else {
          setError(`delete HTTP ${res.status}`);
        }
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [isAuthed, accessToken, lang, loadList, currentSessionId, onNewChat]
  );

  // ---- Render helpers ----

  const renderGroup = (key: GroupKey) => {
    const items = grouped[key];
    if (items.length === 0) return null;
    return (
      <div key={key} className="ask-history__group">
        <div className="ask-history__group-label">{groupLabel(key, lang)}</div>
        <ul className="ask-history__list" role="list">
          {items.map((s) => {
            const active = s.session_id === currentSessionId;
            return (
              <li
                key={s.session_id}
                className={
                  active
                    ? 'ask-history__item ask-history__item--active'
                    : 'ask-history__item'
                }
              >
                <button
                  type="button"
                  className="ask-history__item-btn"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => handleSelect(s.session_id)}
                  title={s.title ?? ''}
                >
                  <span className="ask-history__item-title">
                    {s.title ||
                      (lang === 'vi' ? 'Cuộc trò chuyện' : 'Conversation')}
                  </span>
                  <span className="ask-history__item-time">
                    {relativeTime(s.last_turn_at, lang)}
                  </span>
                </button>
                <button
                  type="button"
                  className="ask-history__item-del"
                  aria-label={
                    lang === 'vi'
                      ? 'Xoá cuộc trò chuyện'
                      : 'Delete conversation'
                  }
                  onClick={(e) => handleDelete(s.session_id, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDelete(s.session_id, e);
                    }
                  }}
                >
                  <IconTrash />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Drawer hidden when !isOpen.
  if (isDrawer && !isOpen) return null;

  const rootClass = isDrawer ? 'ask-history ask-history--drawer' : 'ask-history ask-history--sidebar';

  const content = (
    <nav
      ref={navRef}
      className={rootClass}
      role="navigation"
      aria-label={lang === 'vi' ? 'Lịch sử trò chuyện' : 'Chat history'}
    >
      <div className="ask-history__header">
        {isDrawer ? (
          <>
            <h2 className="ask-history__title">
              {lang === 'vi' ? 'Lịch sử trò chuyện' : 'Chat history'}
            </h2>
            <button
              type="button"
              className="ask-history__close"
              onClick={onClose}
              aria-label={lang === 'vi' ? 'Đóng' : 'Close'}
            >
              <IconClose />
            </button>
          </>
        ) : (
          <Button
            variant="primary"
            size="md"
            onClick={onNewChat}
            className="ask-history__new"
            aria-label={lang === 'vi' ? 'Hỏi mới' : 'New chat'}
          >
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <IconPlus />
              <span>{lang === 'vi' ? 'Hỏi mới' : 'New chat'}</span>
            </span>
          </Button>
        )}
      </div>

      <div className="ask-history__search-wrap">
        <Input
          variant="search"
          inputSize="md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            lang === 'vi' ? 'Tìm cuộc trò chuyện...' : 'Search chats...'
          }
          aria-label={
            lang === 'vi' ? 'Tìm cuộc trò chuyện' : 'Search chats'
          }
        />
      </div>

      <div className="ask-history__scroll">
        {loading && (
          <div className="ask-history__hint">
            {lang === 'vi' ? 'Đang tải...' : 'Loading...'}
          </div>
        )}
        {error && (
          <div className="ask-history__hint ask-history__hint--error">
            {error}
          </div>
        )}
        {!loading && sessions.length === 0 && !error && (
          <div className="ask-history__hint">
            {lang === 'vi'
              ? 'Chưa có cuộc trò chuyện nào. Hãy bắt đầu hỏi Asklaw.'
              : 'No conversations yet. Start by asking Asklaw.'}
          </div>
        )}
        {renderGroup('today')}
        {renderGroup('yesterday')}
        {renderGroup('week')}
        {renderGroup('older')}
      </div>

      {!isAuthed && (
        <div className="ask-history__footer">
          <a className="ask-history__signin" href="/auth/login">
            {lang === 'vi' ? 'Đăng nhập để lưu lịch sử' : 'Sign in to save history'}
          </a>
        </div>
      )}
    </nav>
  );

  // Drawer wraps with backdrop overlay.
  if (isDrawer) {
    return (
      <>
        <div
          className="ask-history-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
        {content}
      </>
    );
  }

  return content;
}
